import { FC, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import {
  ModalOverlay,
  ModalBox,
  Header,
  CloseButton,
  Title,
  Aside,
  ButtonsRow,
  OkButton,
  CancelButton
} from "./ErrorModal.styles";

import X_SRC from "../../../Icons/X.svg";

import { isSupportAssistantEnabled } from "../../../utility/supportAssistantAccess";
import {
  isSupportAssistantMounted,
  openSupportAssistant,
} from "../../../utility/supportAssistantBus";

interface IErrorModal {
  open: boolean;
  onClose: () => void;
  error: string;
  title?: string;

  /**
   * Offer to carry this failure into the support assistant.
   *
   * ONE optional prop, and the modal learns nothing else about the assistant: the only import
   * that couples them is the bus. A caller that supplies nothing gets exactly today's dialog.
   *
   * `question` is what the composer is pre-filled with — phrase it as the user would ask it,
   * not as the error text. `diagnostics` is the context they should not have to retype (the
   * raw message, an id, the screen) and is never shown in the composer.
   */
  help?: { question: string; diagnostics?: string };

  /**
   * Codes of OTHER failures that arrived while this dialog was already open — see
   * `ErrorDialogHost`, which is the only thing that passes it.
   *
   * Named rather than counted, because a code is the token support asks for and the assistant
   * matches on. Empty or absent renders nothing at all.
   */
  also?: readonly string[];
}

export const ErrorModal: FC<IErrorModal> = ({
  open,
  onClose,
  error,
  title,
  help,
  also
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  /** ✅ Close on ESC */
  useEffect(() => {
    if (!open) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  /** ✅ Close on backdrop click */
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === modalRef.current) onClose();
  };

  if (!open) return null;

  /**
   * ⚠️ BOTH CONDITIONS, AND THE SECOND ONE IS THE POINT.
   *
   * `isSupportAssistantEnabled()` says the feature exists in this environment.
   * `isSupportAssistantMounted()` says the panel is actually there to receive the question —
   * it is false on the screens the host does not cover, and it is what makes the difference
   * between a button that opens the assistant and a button that does nothing when pressed.
   * A dead button is worse than no button: the user concludes the app is broken rather than
   * that the help simply is not offered here.
   *
   * Read at render time, not memoized: both are cheap module reads, and the mounted answer is
   * genuinely allowed to change while a long-lived error dialog sits on screen.
   */
  const canAskAssistant =
    Boolean(help) && isSupportAssistantEnabled() && isSupportAssistantMounted();

  const askAssistant = () => {
    // Close either way. The panel and this dialog both sit at the top of the stacking order,
    // and an error the user has just handed to the assistant is not a thing they still need to
    // dismiss. `openSupportAssistant` returning false cannot happen behind `canAskAssistant`
    // except in the race between the render and the click — in which case closing is still the
    // right outcome, and the error is in the log.
    if (help) openSupportAssistant(help);
    onClose();
  };

  return createPortal(
    <ModalOverlay ref={modalRef} onClick={handleBackdropClick}>
      <ModalBox>
        <Header>
          <div>{title?.length ? title : "CSM Shopfloor"}</div>
          <CloseButton onClick={onClose}>
            <img src={X_SRC} alt="close" />
          </CloseButton>
        </Header>

        <Title>{error}</Title>

        {also && also.length > 0 && (
          <Aside>
            {also.length === 1
              ? `Another problem was reported at the same time: ${also[0]}.`
              : `${also.length} other problems were reported at the same time: ${also.join(", ")}.`}
          </Aside>
        )}

        <ButtonsRow>
          {canAskAssistant && (
            <CancelButton type="button" onClick={askAssistant}>
              Ask CoSMo about this
            </CancelButton>
          )}
          <OkButton onClick={onClose}>OK</OkButton>
        </ButtonsRow>
      </ModalBox>
    </ModalOverlay>,
    document.body
  );
};